import { put, select, takeEvery } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import { Action } from 'redux-actions';
import { Language } from '../../blocks/localization/Language';
import AccountService from '../../services/account.service';
import { ActivateFullProfileActionTypes, ChangeLanguageActionTypes, ChangePasswordActionTypes, RefreshProfilesActionTypes, ForgotPasswordActionTypes, GetAccountActionTypes, GetIsFullProfileActivationInProgressActionTypes, RegisterActionTypes, ResetPasswordActionTypes, UpdateUserActionTypes, VerifyActivationMailActionTypes, VerifyEmailActionTypes } from './types';
import { activateFullProfileActions, changeLanguageActions, changePasswordActions, refreshProfilesActions, forgotPasswordActions, getAccountActions, getIsFullProfileActivationInProgressActions, registerActions, resetPasswordActions, updateUserActions, verifyActivationMailActions, verifyEmailActions } from './actions';
import sagaUtility from '../../blocks/utils/saga-utility';
import { RegisterRequest } from '../../models/account/register/RegisterRequest';
import I18n from 'i18n-js';
import Toast from '../../components/shared/Toast';
import { ForgotPasswordRequest } from '../../models/account/forgot-password/ForgotPasswordRequest';
import { ResetPasswordRequest } from '../../models/account/reset-password/ResetPasswordRequest';
import { ChangePasswordRequest } from '../../models/account/change-password/ChangePasswordRequest';
import { VerifyActivationMailRequest } from '../../models/account/verify-activation-mail/VerifyActivationMailRequest';
import RoutesConst from '../../blocks/consts/RoutesConst';
import Account from '../../models/account/Account';
import { getClientActions, getClientCouponActions, getClientSubsidiesActions } from '../client/actions';
import { UpdateUserRequest } from '../../models/account/update-user/UpdateUserRequest';
import { getTrafficAreasActions, getTrafficAreasGeographyActions } from '../tariff/actions';
import { ApplicationState } from '..';
import { VerifyEmailRequest } from '../../models/account/verify-email/VerifyEmailRequest';
import { ActivateFullProfileRequest } from '../../models/profile/ActivateProfileRequest';
import { MediumType } from '../../models/profile/MediumType';
import SessionStorageConst from '../../blocks/consts/SessionStorageConst';
import JoinWithClientRequest from '../../models/account/JoinWithClientRequest';
import { logoutActions } from '../auth/actions';

export default function* accountSaga() {
    yield takeEvery(ChangeLanguageActionTypes.REQUEST, changeLanguageRequest);
    yield takeEvery(ChangeLanguageActionTypes.SUCCESS, changeLanguageSuccess);

    yield takeEvery(GetAccountActionTypes.REQUEST, getAccountRequest);
    yield takeEvery(GetAccountActionTypes.SUCCESS, getAccountSuccess);

    yield takeEvery(RegisterActionTypes.REQUEST, registerRequest);
    yield takeEvery(RegisterActionTypes.SUCCESS, registerSuccess);

    yield takeEvery(ForgotPasswordActionTypes.REQUEST, forgotPasswordRequest);
    yield takeEvery(ForgotPasswordActionTypes.SUCCESS, forgotPasswordSuccess);

    yield takeEvery(ResetPasswordActionTypes.REQUEST, resetPasswordRequest);
    yield takeEvery(ResetPasswordActionTypes.SUCCESS, resetPasswordSuccess);

    yield takeEvery(ChangePasswordActionTypes.REQUEST, changePasswordRequest);
    yield takeEvery(ChangePasswordActionTypes.SUCCESS, changePasswordSuccess);

    yield takeEvery(UpdateUserActionTypes.REQUEST, updateUserRequest);
    yield takeEvery(UpdateUserActionTypes.SUCCESS, updateUserSuccess);

    yield takeEvery(VerifyActivationMailActionTypes.REQUEST, verifyActivationMailRequest);
    yield takeEvery(VerifyActivationMailActionTypes.SUCCESS, verifyActivationMailSuccess);

    yield takeEvery(VerifyEmailActionTypes.REQUEST, verifyEmailRequest);

    yield takeEvery(ActivateFullProfileActionTypes.REQUEST, activateFullProfileRequest);
    yield takeEvery(ActivateFullProfileActionTypes.SUCCESS, activateFullProfileSuccess);

    yield takeEvery(GetIsFullProfileActivationInProgressActionTypes.REQUEST, getIsFullProfileActivationInProgressRequest);
    yield takeEvery(GetIsFullProfileActivationInProgressActionTypes.SUCCESS, getIsFullProfileActivationInProgressSuccess);

    yield takeEvery(RefreshProfilesActionTypes.REQUEST, refreshProfilesRequest);
    yield takeEvery(RefreshProfilesActionTypes.SUCCESS, refreshProfilesSuccess);
}

function* changeLanguageRequest(action: Action<Language>) {
    const language = action.payload as Language;
    sessionStorage.setItem(SessionStorageConst.Language, language);

    const account: Account | undefined = yield select((state: ApplicationState) => state.account.account);
    if (account) {
        yield sagaUtility(undefined, AccountService.changeLanguage, changeLanguageActions.success, changeLanguageActions.failure);
    } else {
        yield put(changeLanguageActions.success());
    }
}

function* changeLanguageSuccess(action: Action<any>) {
    window.location.reload();
}

function* getAccountRequest(action: Action<any>) {
    yield sagaUtility(action.payload, AccountService.get, getAccountActions.success, getAccountActions.failure);
}

function* getAccountSuccess(action: Action<Account>) {
    yield put(getTrafficAreasActions.request());
    yield put(getTrafficAreasGeographyActions.request());

    if (action.payload.clientId) {
        yield put(getClientActions.request());
        yield put(getClientCouponActions.request());
        yield put(getClientSubsidiesActions.request());
    }
}

function* registerRequest(action: Action<RegisterRequest>) {
    yield sagaUtility(action.payload, AccountService.register, registerActions.success, registerActions.failure, true);
}

function* registerSuccess(action: Action<any>) {
    const registerRequest = action.payload.request as RegisterRequest;

    const successMessage = I18n.t('register.successText', { email: registerRequest.email });
    Toast.notifySuccess(successMessage);

    yield put(push(RoutesConst.Home));
}

function* forgotPasswordRequest(action: Action<ForgotPasswordRequest>) {
    yield sagaUtility(action.payload, AccountService.forgotPassword, forgotPasswordActions.success, forgotPasswordActions.failure);
}

function* forgotPasswordSuccess(action: Action<any>) {
    Toast.notifySuccess(I18n.t('forgotPassword.text'));
    yield put(push(RoutesConst.Home));
}

function* resetPasswordRequest(action: Action<ResetPasswordRequest>) {
    yield sagaUtility(action.payload, AccountService.resetPassword, resetPasswordActions.success, resetPasswordActions.failure);
}

function* resetPasswordSuccess(action: Action<any>) {
    Toast.notifySuccess(I18n.t('resetPassword.success'));
    yield put(push(RoutesConst.Home));
}

function* changePasswordRequest(action: Action<ChangePasswordRequest>) {
    yield sagaUtility(action.payload, AccountService.changePassword, changePasswordActions.success, changePasswordActions.failure);
}

function* changePasswordSuccess(action: Action<any>) {
    Toast.notifySuccess(I18n.t('myProfile.changePasswordSuccess'));
}

function* updateUserRequest(action: Action<UpdateUserRequest>) {
    yield sagaUtility(action.payload, AccountService.updateUser, updateUserActions.success, updateUserActions.failure, true);
}

function* updateUserSuccess(action: Action<any>) {
    const updateUserRequest = action.payload.request as UpdateUserRequest;

    const accountEmail: string = yield select((state: ApplicationState) => state.account.account?.email);

    if (accountEmail === updateUserRequest.email) {
        Toast.notifySuccess(I18n.t('myProfile.updateUserSuccess'));
    } else {
        Toast.notifySuccess(I18n.t('myProfile.updateUserSuccessWithMail', { email: updateUserRequest.email }));
    }

    yield put(getAccountActions.request());
}

function* verifyActivationMailRequest(action: Action<VerifyActivationMailRequest>) {
    yield sagaUtility(action.payload, AccountService.verifyActivationMail, verifyActivationMailActions.success, verifyActivationMailActions.failure);
}

function* verifyActivationMailSuccess(action: Action<any>) {
    Toast.notifySuccess(I18n.t('verifyActivationMail.success'));
    yield put(push(RoutesConst.Login));
}

function* verifyEmailRequest(action: Action<VerifyEmailRequest>) {
    yield sagaUtility(action.payload, AccountService.verifyEmail, verifyEmailActions.success, verifyEmailActions.failure);
}

function* activateFullProfileRequest(action: Action<ActivateFullProfileRequest>) {
    const request = action.payload;

    if (request.mediumType == MediumType.Mobile) {
        const confirmUsageMediumChangeRequest = request.verificationCode;
        yield sagaUtility(confirmUsageMediumChangeRequest, AccountService.confirmUsageMediumChange, activateFullProfileActions.success, activateFullProfileActions.failure);
    }
    else if (request.mediumType == MediumType.Physical) {
        const joinWithClientRequest = { cardSerialNumber: request.cardNumber, identificationNumber: request.oib } as JoinWithClientRequest;
        yield sagaUtility(joinWithClientRequest, AccountService.joinWithClient, activateFullProfileActions.success, activateFullProfileActions.failure);
    }
}

function* activateFullProfileSuccess(action: Action<any>) {
    Toast.notifySuccess(I18n.t('activateFullProfile.processStartedSuccessMessage'));
    yield put(push(RoutesConst.Dashboard));
}

function* getIsFullProfileActivationInProgressRequest(action: Action<VerifyEmailRequest>) {
    yield sagaUtility(action.payload, AccountService.getIsFullProfileActivationInProgress, getIsFullProfileActivationInProgressActions.success, getIsFullProfileActivationInProgressActions.failure, false, false);
}

function* getIsFullProfileActivationInProgressSuccess(action: Action<any>) {
    const isFullProfileActivationInProgress = action.payload.isFullProfileActivationInProgress;
    if (isFullProfileActivationInProgress === false) {
        yield put(logoutActions.request());
    }
}

function* refreshProfilesRequest(action: Action<number>) {
    yield sagaUtility(action.payload, AccountService.refreshProfiles, refreshProfilesActions.success, refreshProfilesActions.failure);
}

function *refreshProfilesSuccess(action: Action<any>) {
    Toast.notifySuccess(I18n.t("myProfile.tabs.myProfile.profiles.processStarted"));
}